import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { decodeEntities } from '../lib/decodeEntities'

const API_URL = import.meta.env.VITE_API_URL ?? ''
const DEBOUNCE_MS = 300
const MIN_QUERY_LEN = 2

/**
 * Username typeahead for the navbar.
 *
 * - Debounced 300ms via useRef + setTimeout (mirrors BrowsePage:163-164).
 * - Anon-reachable; server enforces rate limit + safety filters.
 * - Result rows mirror FollowListTab (UserProfile:1092-1149) — inline
 *   img-or-gradient avatar, displayName, @username, follower count.
 * - Keyboard: Arrow keys navigate, Enter selects, Escape closes.
 * - Outside-click closes (mousedown listener like NotificationPanel).
 *
 * @param {Object} props
 * @param {string} [props.className] - Extra class for the wrapper (e.g. for
 *   mobile-menu styling tweaks).
 * @param {() => void} [props.onNavigate] - Called after a result is selected
 *   (used by the mobile menu to close itself).
 * @param {boolean} [props.autoFocus] - If true, focuses the input on mount
 *   (used by the mobile search sheet).
 */
export default function UserSearchDropdown({ className = '', onNavigate, autoFocus = false }) {
  const navigate = useNavigate()
  const [inputVal, setInputVal] = useState('')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [activeIdx, setActiveIdx] = useState(-1)
  const [requestedQuery, setRequestedQuery] = useState('')

  const wrapperRef = useRef(null)
  const inputRef   = useRef(null)
  const debounceRef = useRef(null)
  const reqIdRef    = useRef(0)

  // Strip leading @ from displayed input value (server re-normalises).
  function handleChange(e) {
    const next = e.target.value.replace(/^@+/, '')
    setInputVal(next)
    setActiveIdx(-1)
    const trimmed = next.trim()
    if (trimmed.length < MIN_QUERY_LEN) {
      setOpen(false)
      setResults([])
      setLoading(false)
      clearTimeout(debounceRef.current)
      return
    }
    setOpen(true)
    setLoading(true)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => runSearch(trimmed), DEBOUNCE_MS)
  }

  const runSearch = useCallback(async (q) => {
    const myReq = ++reqIdRef.current
    setRequestedQuery(q)
    try {
      const { data } = await axios.get(`${API_URL}/api/users/search`, {
        params: { q },
      })
      // Drop stale responses if a newer query has been fired.
      if (myReq !== reqIdRef.current) return
      setResults(Array.isArray(data) ? data : [])
    } catch {
      // Server returns [] on its own errors; on network/429 fall back to empty.
      if (myReq !== reqIdRef.current) return
      setResults([])
    } finally {
      if (myReq === reqIdRef.current) setLoading(false)
    }
  }, [])

  // Outside-click close — mirrors Navbar.jsx notif handler shape.
  useEffect(() => {
    function handler(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false)
        setActiveIdx(-1)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Cleanup pending debounce on unmount.
  useEffect(() => () => clearTimeout(debounceRef.current), [])

  // Optional autofocus (used by the mobile search sheet).
  useEffect(() => {
    if (autoFocus) inputRef.current?.focus()
  }, [autoFocus])

  function closeAndClear() {
    setOpen(false)
    setInputVal('')
    setResults([])
    setActiveIdx(-1)
  }

  function selectUser(username) {
    closeAndClear()
    if (typeof onNavigate === 'function') onNavigate()
    navigate(`/user/${username}`)
  }

  function handleKeyDown(e) {
    if (e.key === 'Escape') {
      e.preventDefault()
      setOpen(false)
      setActiveIdx(-1)
      inputRef.current?.blur()
      return
    }
    if (!open || results.length === 0) {
      // Allow Enter to be a no-op when there's nothing to select.
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIdx(i => (i + 1) % results.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIdx(i => (i <= 0 ? results.length - 1 : i - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const pick = activeIdx >= 0 ? results[activeIdx] : results[0]
      if (pick) selectUser(pick.username)
    }
  }

  function handleFocus() {
    if (inputVal.trim().length >= MIN_QUERY_LEN) setOpen(true)
  }

  const trimmedInput = inputVal.trim()
  const showResults  = open && trimmedInput.length >= MIN_QUERY_LEN
  const showEmpty    = showResults && !loading && results.length === 0 && requestedQuery === trimmedInput

  return (
    <div className={`user-search ${className}`.trim()} ref={wrapperRef}>
      <input
        ref={inputRef}
        type="text"
        className="user-search__input"
        placeholder="Search users…"
        value={inputVal}
        onChange={handleChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        autoComplete="off"
        aria-label="Search users"
        aria-autocomplete="list"
        aria-expanded={showResults}
        aria-controls="user-search-listbox"
      />
      {showResults && (
        <div
          id="user-search-listbox"
          className="user-search__panel"
          role="listbox"
        >
          {loading && (
            <div className="user-search__status">Searching…</div>
          )}
          {!loading && results.map((u, idx) => {
            const isActive = idx === activeIdx
            const initial = (u.displayName || u.username || '?')[0].toUpperCase()
            return (
              <button
                key={u.id}
                type="button"
                className={`user-search__row${isActive ? ' user-search__row--active' : ''}`}
                role="option"
                aria-selected={isActive}
                onMouseEnter={() => setActiveIdx(idx)}
                onClick={() => selectUser(u.username)}
              >
                <div
                  className="user-search__avatar"
                  style={{ background: u.avatarUrl ? undefined : u.avatarGrad }}
                >
                  {u.avatarUrl
                    ? <img src={u.avatarUrl} alt={u.displayName} />
                    : initial}
                </div>
                <div className="user-search__meta">
                  <span className="user-search__name">{decodeEntities(u.displayName)}</span>
                  <span className="user-search__handle">@{u.username}</span>
                  {u.followers > 0 && (
                    <span className="user-search__followers">
                      {u.followers.toLocaleString()} {u.followers === 1 ? 'follower' : 'followers'}
                    </span>
                  )}
                </div>
              </button>
            )
          })}
          {showEmpty && (
            <div className="user-search__status">No users found.</div>
          )}
        </div>
      )}
    </div>
  )
}
